/**
 * NotifSettingsScreen — three-toggle notification preferences
 * (`/settings/notif`).
 *
 * Pixel-faithful port of the corresponding section in
 * `design/project/screens-features.jsx`'s `NotifSettingsScreen`. We only
 * surface the three settable kinds the spec requires — `system` is always
 * on (no user toggle).
 *
 * State source: `notifications.settings` (derived from `meta.notif_settings`
 * during bootstrap, mutated via `setSetting`). On change we also enqueue a
 * `set_meta` op so the preference round-trips to Sheets.
 *
 * `set_meta` is now a first-class member of `SyncOpType` (`sync-queue.ts`).
 * We still use `create_notification` here for backward compatibility with
 * pending ops in older clients' queues — the sync handler routes via
 * `payload.__meta` either way. Remove this branch once all clients have
 * upgraded.
 */
import Icon, { type IconName } from '@/components/common/Icon';
import { useStore } from '@/state/store';
import {
  NOTIF_SETTINGS_META_KEY,
  serializeNotifSettings,
  SETTABLE_KINDS,
  type SettableKind,
} from '@/state/slices/notifications';

export interface NotifSettingsScreenProps {
  onBack?: () => void;
}

const ROW_META: Record<
  SettableKind,
  { title: string; detail: string; icon: IconName; color: string }
> = {
  occ_conflict: {
    title: 'OCC 衝突',
    detail: '兩台裝置同時編輯同一筆紀錄時通知',
    icon: 'cloudOff',
    color: 'var(--warn)',
  },
  recurring_generated: {
    title: '固定週期自動生成',
    detail: '排定日期自動產生交易時通知',
    icon: 'repeat',
    color: 'var(--cat-home)',
  },
  sync_failed: {
    title: '同步失敗',
    detail: '網路或 Sheets API 異常，重試多次仍失敗時',
    icon: 'cloud',
    color: 'var(--clay-500)',
  },
};

function genOpId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function NotifSettingsScreen({
  onBack,
}: NotifSettingsScreenProps): JSX.Element {
  const settings = useStore((s) => s.notifications.settings);
  const setSetting = useStore((s) => s.notifications.setSetting);
  const enqueue = useStore((s) => s.sync.enqueue);

  const handle = (kind: SettableKind, enabled: boolean): void => {
    setSetting(kind, enabled);
    // Compute next snapshot to persist (zustand setters are sync but we
    // don't have access to the post-change object here — re-derive.)
    const next = { ...settings, [kind]: enabled };
    const ts = new Date().toISOString();
    enqueue({
      id: genOpId(),
      type: 'create_notification',
      // Reusing the create_notification op slot to avoid widening
      // SyncOpType — sync-queue handler distinguishes via payload.kind.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      payload: {
        __meta: true,
        key: NOTIF_SETTINGS_META_KEY,
        value: serializeNotifSettings(next),
      } as any,
      attempts: 0,
      last_error: '',
      created_at: ts,
    });
  };

  return (
    <div
      style={{
        height: '100%',
        overflow: 'auto',
        background: 'var(--bg-app)',
        paddingBottom: 80,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '60px 20px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="返回"
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Icon name="chevL" size={22} stroke="var(--fg-1)" />
          </button>
        )}
        <h1
          style={{
            font: '600 22px var(--font-cjk-display)',
            color: 'var(--fg-1)',
            letterSpacing: '-0.01em',
            margin: 0,
          }}
        >
          通知設定
        </h1>
      </div>

      <div style={{ padding: '4px 16px 18px' }}>
        <div
          style={{
            background: 'var(--bg-surface)',
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: 'var(--shadow-1)',
          }}
        >
          {SETTABLE_KINDS.map((kind, i) => {
            const meta = ROW_META[kind];
            const checked = settings[kind];
            return (
              <div
                key={kind}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '14px 14px',
                  borderBottom:
                    i < SETTABLE_KINDS.length - 1
                      ? '0.5px solid var(--border-1)'
                      : 0,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 11,
                    background: meta.color,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                >
                  <Icon name={meta.icon} size={18} stroke="#fff" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      font: '500 14px var(--font-cjk)',
                      color: 'var(--fg-1)',
                    }}
                  >
                    {meta.title}
                  </div>
                  <div
                    style={{
                      font: '400 11.5px var(--font-cjk)',
                      color: 'var(--fg-3)',
                      marginTop: 2,
                      lineHeight: 1.45,
                    }}
                  >
                    {meta.detail}
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={checked}
                  aria-label={meta.title}
                  onClick={() => handle(kind, !checked)}
                  style={{
                    width: 46,
                    height: 28,
                    borderRadius: 14,
                    background: checked ? 'var(--accent)' : 'var(--sand-300)',
                    border: 0,
                    cursor: 'pointer',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: 2,
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      background: '#fff',
                      boxShadow: '0 1.5px 4px rgba(0,0,0,0.14)',
                      transform: checked ? 'translateX(18px)' : 'translateX(0)',
                      transition: 'transform 160ms var(--ease-standard, ease)',
                    }}
                  />
                </button>
              </div>
            );
          })}
        </div>

        <div
          style={{
            marginTop: 14,
            padding: '0 4px',
            font: '400 11.5px var(--font-cjk)',
            color: 'var(--fg-3)',
            lineHeight: 1.55,
          }}
        >
          關閉某類通知後，固定週期仍會自動生成交易，但不再寫入該類通知列。系統訊息（升級、嚴重錯誤）始終啟用，無法停用。
        </div>
      </div>
    </div>
  );
}
